import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { Skeleton, useInteractionFlag, appState } from "@fleet/ui";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { useTheme } from "@/providers/theme-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import { OfflineBanner } from "@/components/offline-banner";
import { ListThemeProvider } from "@/providers/list-theme-provider";
import { CommandPalette } from "@/features/search/components/command-palette";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { SkipToContent } from "@/components/skip-to-content";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";

// Layout module parts
import { AppLayoutContext } from "../context";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppThemeState } from "../hooks/use-app-theme-state";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";

interface AppLayoutContentProps {
  // May be null on the first frames while PowerSync's local SQLite is still
  // initializing. The static shell renders regardless; the content region
  // shows a granular skeleton until the DB is ready.
  db: AbstractPowerSyncDatabase | null;
}

/**
 * Granular skeleton for the routed content region, shown while the local
 * PowerSync DB is not yet available. Mirrors the spacing of typical list/detail
 * screens so the shell does not visibly reflow once content mounts.
 */
function ContentSkeleton() {
  return (
    <div class="flex flex-1 flex-col gap-4 p-4 sm:p-6" aria-hidden="true">
      <Skeleton class="h-8 w-48" />
      <div class="flex flex-col gap-3">
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-3/4" />
      </div>
    </div>
  );
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

const dummyStats = () => undefined;

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  const [committedPath, setCommittedPath] = createSignal(location().pathname);

  createEffect(() => {
    const path = location().pathname;
    setCommittedPath(path);
  });

  const isNavigating = () => routerState().status === "pending";

  // Key for the per-page entrance animation. We key on the LEAF route id (the
  // route pattern), NOT the pathname: navigating between different screens
  // changes the id and replays the slide-up entrance, while the reader's
  // chapter-to-chapter swipe keeps the SAME id (only params change), so the
  // entrance never re-fires mid-swipe and never fights the swipe transform.
  const leafRouteId = () => routerState().matches.at(-1)?.routeId ?? "";

  const theme = useTheme();

  const { isSidebarOpen, setIsSidebarOpen, shortcutsOpen, setShortcutsOpen } =
    useAppDialogs(location);

  // The mobile drawer, while open, owns horizontal drag
  // (drag-to-dismiss): other interaction context consumers must
  // cederle el paso.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // `t` is NOT passed: hook never used it (it was a dead parameter that only allowed
  // the lax `(key: string) => string` type to slip plain strings through the back door).
  useAppShortcuts({
    searchPalette,
    location,
    navigate,
    theme,
    shortcutsOpen,
    setShortcutsOpen,
    setIsSidebarOpen,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  const { listContextId, activeBlur, activeListTokens, activeListBackground, glows } =
    useAppThemeState({
      committedPath: () => location().pathname,
      theme,
    });

  return (
    <ListThemeProvider listId={listContextId()}>
      <SkipToContent />
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeListBackground()}
        patternType={activeListTokens()?.patternType ?? theme.tokens()?.patternType}
        patternOpacity={activeListTokens()?.patternOpacity ?? theme.tokens()?.patternOpacity}
        {...glows()}
      >
        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} stats={dummyStats} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          id="main-content"
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
            <AppLayoutContext.Provider
              value={{
                committedPath: () => committedPath(),
              }}
            >
              <div class="flex flex-1 flex-col min-h-0 h-full">
                <Show when={props.db} fallback={<ContentSkeleton />}>
                  <AppErrorBoundary variant="region" scope="content">
                    {/*
                      Per-page entrance: keyed on the LEAF route id so the
                      slide-up+fade replays when switching screens but NOT on the
                      reader's chapter swipe (same id, params only). GPU-first
                      (transform/opacity only) and collapsed by the global
                      prefers-reduced-motion rule. The wrapper keeps the flex
                      column layout so there is no layout shift on mount.
                    */}
                    <Show when={leafRouteId()} keyed>
                      <div class="flex flex-1 flex-col min-h-0 h-full motion-safe:animate-slide-in-up motion-safe:will-change-[transform,opacity]">
                        <Outlet />
                      </div>
                    </Show>
                  </AppErrorBoundary>
                </Show>
              </div>
            </AppLayoutContext.Provider>
          </div>

          {/* Mobile-first primary navigation (legacy reading sections) */}
          <MobileTabBar onOpenMenu={() => setIsSidebarOpen(true)} />
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />

        {/* Multi-account switcher overlay — mounted ONCE at the layout level
            (the sidebar renders twice: mobile drawer + desktop rail). Opened
            URL-addressably via `?account=switch`. */}
        <AccountSwitcherHost />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />
    </ListThemeProvider>
  );
}
